/**
 * Comprehensive Notification Management Dashboard
 * Provides access to all notification management features
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Smartphone as SmartphoneIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Event as EventIcon,
  MonetizationOn as MonetizationOnIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminIcon,
  BugReport as BugReportIcon,
  Science as TestTubeIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Share as ShareIcon,
  Description as DocumentIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  LocalPharmacy as PharmacyIcon,
  Favorite as HeartIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon,
  Gavel as GavelIcon,
  Policy as PolicyIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  DataUsage as DataUsageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// API Services
import {
  notificationDeliveryApi,
  notificationPreferencesApi,
  notificationTemplatesApi,
  notificationSchedulingApi,
  notificationHistoryApi,
  notificationAnalyticsApi,
  notificationErrorApi,
  notificationTestingApi
} from '../../services/api/notificationManagement';

const NotificationManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Data states
  const [deliveryData, setDeliveryData] = useState(null);
  const [preferencesData, setPreferencesData] = useState(null);
  const [templatesData, setTemplatesData] = useState([]);
  const [scheduledData, setScheduledData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [errorLogsData, setErrorLogsData] = useState(null);
  
  // Dialog states
  const [showPreferencesDialog, setShowPreferencesDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);

  // Form states
  const [preferencesForm, setPreferencesForm] = useState({
    email_enabled: true,
    sms_enabled: true,
    whatsapp_enabled: false,
    in_app_enabled: true,
    appointment_reminders: true,
    payment_notifications: true,
    marketing_emails: false
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    event_type: '',
    channel: 'EMAIL',
    subject_template: '',
    body_template: ''
  });

  const [scheduleForm, setScheduleForm] = useState({
    event_type: '',
    channel: 'EMAIL',
    subject: '',
    body: '',
    scheduled_for: '',
    priority: 'NORMAL'
  });

  const [testForm, setTestForm] = useState({
    channel: 'IN_APP',
    message: 'Test notification message'
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all notification data in parallel
      const [
        deliveryResponse,
        preferencesResponse,
        templatesResponse,
        scheduledResponse,
        historyResponse,
        analyticsResponse,
        errorLogsResponse
      ] = await Promise.allSettled([
        notificationDeliveryApi.getDeliveryStatus(),
        notificationPreferencesApi.getPreferences(),
        notificationTemplatesApi.getTemplates(),
        notificationSchedulingApi.getScheduled(),
        notificationHistoryApi.getHistory(),
        notificationAnalyticsApi.getAnalytics(),
        notificationErrorApi.getErrorLogs()
      ]);

      // Set data from successful responses
      if (deliveryResponse.status === 'fulfilled') {
        setDeliveryData(deliveryResponse.value);
      }
      if (preferencesResponse.status === 'fulfilled') {
        setPreferencesData(preferencesResponse.value);
        setPreferencesForm(preferencesResponse.value);
      }
      if (templatesResponse.status === 'fulfilled') {
        setTemplatesData(templatesResponse.value);
      }
      if (scheduledResponse.status === 'fulfilled') {
        setScheduledData(scheduledResponse.value);
      }
      if (historyResponse.status === 'fulfilled') {
        setHistoryData(historyResponse.value.notifications || []);
      }
      if (analyticsResponse.status === 'fulfilled') {
        setAnalyticsData(analyticsResponse.value);
      }
      if (errorLogsResponse.status === 'fulfilled') {
        setErrorLogsData(errorLogsResponse.value);
      }

    } catch (err) {
      console.error('Error loading notification data:', err);
      setError('Failed to load notification data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesUpdate = async () => {
    try {
      setLoading(true);
      await notificationPreferencesApi.updatePreferences(preferencesForm);
      toast.success('Notification preferences updated successfully!');
      setShowPreferencesDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error updating preferences:', err);
      toast.error('Failed to update preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateCreate = async () => {
    try {
      setLoading(true);
      await notificationTemplatesApi.createTemplate(templateForm);
      toast.success('Notification template created successfully!');
      setShowTemplateDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error creating template:', err);
      toast.error('Failed to create template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleNotification = async () => {
    try {
      setLoading(true);
      await notificationSchedulingApi.scheduleNotification(scheduleForm);
      toast.success('Notification scheduled successfully!');
      setShowScheduleDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error scheduling notification:', err);
      toast.error('Failed to schedule notification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setLoading(true);
      await notificationDeliveryApi.sendTestNotification(testForm);
      toast.success('Test notification sent successfully!');
      setShowTestDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error sending test notification:', err);
      toast.error('Failed to send test notification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, onClick, loading, trend, trendValue }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {loading ? <CircularProgress size={20} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trend === 'up' ? (
              <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
            ) : (
              <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
            )}
            <Typography variant="caption" color={trend === 'up' ? 'success.main' : 'error.main'}>
              {trendValue}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const FeatureCard = ({ title, description, icon, color, onClick, status, count }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          {count !== undefined && (
            <Badge badgeContent={count} color="primary">
              <Chip 
                label={status} 
                size="small" 
                color={status === 'Active' ? 'success' : 'default'}
              />
            </Badge>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={onClick}>
          Manage
        </Button>
      </CardActions>
    </Card>
  );

  if (loading && !deliveryData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Notification Management Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Notifications"
            value={deliveryData?.total_notifications || 0}
            icon={<NotificationIcon />}
            color="primary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Sent Notifications"
            value={deliveryData?.sent_notifications || 0}
            icon={<CheckIcon />}
            color="success.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Failed Notifications"
            value={deliveryData?.failed_notifications || 0}
            icon={<ErrorIcon />}
            color="error.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Delivery Rate"
            value={`${deliveryData?.delivery_rate || 0}%`}
            icon={<AnalyticsIcon />}
            color="info.main"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Feature Management */}
      <Grid container spacing={3}>
        {/* Delivery Status */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Delivery Status"
            description="Monitor notification delivery and status"
            icon={<NotificationIcon />}
            color="primary.main"
            onClick={() => navigate('/notification-dashboard/delivery')}
            status="Active"
          />
        </Grid>

        {/* Preferences */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Notification Preferences"
            description="Manage your notification preferences"
            icon={<SettingsIcon />}
            color="secondary.main"
            onClick={() => setShowPreferencesDialog(true)}
            status="Configured"
          />
        </Grid>

        {/* Templates */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Notification Templates"
            description="Create and manage notification templates"
            icon={<DocumentIcon />}
            color="info.main"
            onClick={() => setShowTemplateDialog(true)}
            status={templatesData.length > 0 ? 'Active' : 'Empty'}
            count={templatesData.length}
          />
        </Grid>

        {/* Scheduling */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Notification Scheduling"
            description="Schedule notifications for future delivery"
            icon={<ScheduleIcon />}
            color="success.main"
            onClick={() => setShowScheduleDialog(true)}
            status={scheduledData.length > 0 ? 'Active' : 'Empty'}
            count={scheduledData.length}
          />
        </Grid>

        {/* History */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Notification History"
            description="View notification history and logs"
            icon={<HistoryIcon />}
            color="warning.main"
            onClick={() => navigate('/notification-dashboard/history')}
            status={historyData.length > 0 ? 'Active' : 'Empty'}
            count={historyData.length}
          />
        </Grid>

        {/* Analytics */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Analytics Dashboard"
            description="View notification analytics and metrics"
            icon={<AnalyticsIcon />}
            color="primary.main"
            onClick={() => setShowAnalyticsDialog(true)}
            status="Available"
          />
        </Grid>

        {/* Error Handling */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Error Handling"
            description="Monitor and debug notification errors"
            icon={<ErrorIcon />}
            color="error.main"
            onClick={() => navigate('/notification-dashboard/errors')}
            status="Available"
          />
        </Grid>

        {/* Testing */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Testing Tools"
            description="Test notification system and channels"
            icon={<TestTubeIcon />}
            color="info.main"
            onClick={() => setShowTestDialog(true)}
            status="Available"
          />
        </Grid>
      </Grid>

      {/* Preferences Dialog */}
      <Dialog open={showPreferencesDialog} onClose={() => setShowPreferencesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Notification Preferences</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Channel Preferences</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferencesForm.email_enabled}
                    onChange={(e) => setPreferencesForm({ ...preferencesForm, email_enabled: e.target.checked })}
                  />
                }
                label="Email Notifications"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferencesForm.sms_enabled}
                    onChange={(e) => setPreferencesForm({ ...preferencesForm, sms_enabled: e.target.checked })}
                  />
                }
                label="SMS Notifications"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferencesForm.whatsapp_enabled}
                    onChange={(e) => setPreferencesForm({ ...preferencesForm, whatsapp_enabled: e.target.checked })}
                  />
                }
                label="WhatsApp Notifications"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferencesForm.in_app_enabled}
                    onChange={(e) => setPreferencesForm({ ...preferencesForm, in_app_enabled: e.target.checked })}
                  />
                }
                label="In-App Notifications"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Content Preferences</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferencesForm.appointment_reminders}
                    onChange={(e) => setPreferencesForm({ ...preferencesForm, appointment_reminders: e.target.checked })}
                  />
                }
                label="Appointment Reminders"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferencesForm.payment_notifications}
                    onChange={(e) => setPreferencesForm({ ...preferencesForm, payment_notifications: e.target.checked })}
                  />
                }
                label="Payment Notifications"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferencesForm.marketing_emails}
                    onChange={(e) => setPreferencesForm({ ...preferencesForm, marketing_emails: e.target.checked })}
                  />
                }
                label="Marketing Emails"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreferencesDialog(false)}>Cancel</Button>
          <Button onClick={handlePreferencesUpdate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update Preferences'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onClose={() => setShowTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Notification Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={templateForm.event_type}
                  onChange={(e) => setTemplateForm({ ...templateForm, event_type: e.target.value })}
                  label="Event Type"
                >
                  <MenuItem value="appointment.created">Appointment Created</MenuItem>
                  <MenuItem value="appointment.reminder">Appointment Reminder</MenuItem>
                  <MenuItem value="payment.success">Payment Success</MenuItem>
                  <MenuItem value="payment.failed">Payment Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={templateForm.channel}
                  onChange={(e) => setTemplateForm({ ...templateForm, channel: e.target.value })}
                  label="Channel"
                >
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                  <MenuItem value="IN_APP">In-App</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject Template"
                value={templateForm.subject_template}
                onChange={(e) => setTemplateForm({ ...templateForm, subject_template: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Body Template"
                value={templateForm.body_template}
                onChange={(e) => setTemplateForm({ ...templateForm, body_template: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
          <Button onClick={handleTemplateCreate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onClose={() => setShowScheduleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Schedule Notification</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={scheduleForm.event_type}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, event_type: e.target.value })}
                  label="Event Type"
                >
                  <MenuItem value="appointment.reminder">Appointment Reminder</MenuItem>
                  <MenuItem value="payment.reminder">Payment Reminder</MenuItem>
                  <MenuItem value="marketing.promotion">Marketing Promotion</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={scheduleForm.channel}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, channel: e.target.value })}
                  label="Channel"
                >
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                  <MenuItem value="IN_APP">In-App</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Schedule For"
                value={scheduleForm.scheduled_for}
                onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_for: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={scheduleForm.priority}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="NORMAL">Normal</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={scheduleForm.subject}
                onChange={(e) => setScheduleForm({ ...scheduleForm, subject: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={scheduleForm.body}
                onChange={(e) => setScheduleForm({ ...scheduleForm, body: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
          <Button onClick={handleScheduleNotification} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Schedule Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={showTestDialog} onClose={() => setShowTestDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Notification</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={testForm.channel}
                  onChange={(e) => setTestForm({ ...testForm, channel: e.target.value })}
                  label="Channel"
                >
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                  <MenuItem value="IN_APP">In-App</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Test Message"
                value={testForm.message}
                onChange={(e) => setTestForm({ ...testForm, message: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestDialog(false)}>Cancel</Button>
          <Button onClick={handleSendTest} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={showAnalyticsDialog} onClose={() => setShowAnalyticsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Notification Analytics</DialogTitle>
        <DialogContent>
          {analyticsData && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Notifications"
                  value={analyticsData.total_notifications}
                  icon={<NotificationIcon />}
                  color="primary.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Sent Notifications"
                  value={analyticsData.sent_notifications}
                  icon={<CheckIcon />}
                  color="success.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Failed Notifications"
                  value={analyticsData.failed_notifications}
                  icon={<ErrorIcon />}
                  color="error.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Success Rate"
                  value={`${analyticsData.success_rate}%`}
                  icon={<AnalyticsIcon />}
                  color="info.main"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAnalyticsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationManagement;
